// THE BOOT DEADLINE (2026-10-08, work.astio.ai "stuck on the loader when
// signed in"). The SDK's REST client has no request timeout, and the boot gate
// awaits its calls one after another (composite boot, getMe, the workspace
// list, the org switch, the scope reads). One request that never answers kept
// the shell on the loader for ever. Every network await of the boot gate now
// races this deadline: a lost request rejects with a BOOT_DEADLINE error, so
// the caller's own catch takes over (a scope read falls back to null, the
// session check shows the loader's error + reload state). The request itself
// is not cancelled; its late answer is ignored.
export const BOOT_CALL_DEADLINE_MS = 10000

// `work` is a promise or a function that returns one (a throw inside the
// function rejects the result the same way).
export function withBootDeadline(work, ms, label) {
  const limit = Number(ms) > 0 ? Number(ms) : 10000
  return new Promise((resolve, reject) => {
    let done = false
    const timer = setTimeout(() => {
      if (done) return
      done = true
      const err = new Error(`boot request "${label || 'call'}" did not answer in ${limit} ms`)
      err.code = 'BOOT_DEADLINE'
      err.name = 'TimeoutError'
      err.label = label || 'call'
      reject(err)
    }, limit)
    let p
    try {
      p = typeof work === 'function' ? work() : work
    } catch (err) {
      p = Promise.reject(err)
    }
    Promise.resolve(p).then(
      (value) => {
        if (done) return
        done = true
        clearTimeout(timer)
        resolve(value)
      },
      (err) => {
        if (done) return
        done = true
        clearTimeout(timer)
        reject(err)
      }
    )
  })
}

export function isBootDeadline(err) {
  return !!err && err.code === 'BOOT_DEADLINE'
}

// The pre-bundle loader (workspace index.html) shows its error line and the
// reload button on `phase: 'error'`. Published by the boot gate when the
// session check never answered, so the loader never stays on screen for ever.
export function publishBootFailure(reason) {
  if (typeof window === 'undefined') return
  const status = { phase: 'error', step: 1, reason: reason || 'boot_failed' }
  try {
    window.__smblsBootStatus = status
  } catch (_) {
    /* a frozen window object keeps the event path below */
  }
  try {
    if (typeof window.dispatchEvent === 'function' && typeof CustomEvent === 'function') {
      window.dispatchEvent(new CustomEvent('smbls:boot-status', { detail: status }))
    }
  } catch (_) {
    /* no event support: the cached status still reaches a late listener */
  }
}
